import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Button from 'react-bootstrap/esm/Button'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'

import BusinessEntityTreeElement from './BusinessEntityTreeElement.jsx'
import Icon from './Icon.jsx'
import Loadable from './Loadable.jsx'

import { businessEntitiesLoadedSignal, rootBusinessEntitySignal, businessEntitiesErrorSignal, currentBusinessEntitySignal, ancestorBEIDsSetSignal } from '../state/signals/businessEntitySignals.js'

function BusinessEntityTreeRoot({ showTreeSignal, handleToggleTree }) {

    //This is used to manually rerender the component when the contents of expandedIdsSignal.value changes.
    //React cannot rerender components based off of set membership, because the data type that changes must be immutable, and set membership is not.
    const [manualRerenderState, workaround] = useState(false)
    const rerender = () => {
        workaround(!manualRerenderState)
    }

    const { user, config, accessToken, permissionMasks, permissions } = useOutletContext()

    const hiddenBusinessEntitiesSetSignal = useSignal(null)
    const filterSignal = useSignal('')
    const expandAllSignal = useSignal(false)
    const expandedBusinessEntitiesSetSignal = useSignal(null)
    const expandedSearchOverrideSetSignal = useSignal(null)
    const initializedSignal = useSignal(false)
    const treeKeyComputed = useComputed(() => {
        if (!expandedBusinessEntitiesSetSignal.value) {
            return -1
        }
        // this ensures we likely get a unique key each time the tree data changes
        // the purpose of this is to force the tree to rebuild each render to work around
        // an issue in the treeview when you use both selectedid and expandedid props
        // console.log("There are ".concat(expandedIdsSignal.value.size).concat(" expanded BE IDs"))
        return expandedBusinessEntitiesSetSignal.value.size
    })

    useEffect(
        () => {
            if (initializedSignal.value) {
                //console.log("Already initialized expanded BEIDs")
                return
            }
            if (!businessEntitiesLoadedSignal.value) {
                //console.log("Business entities have not yet been loaded")
                return
            }
            let set = new Set([currentBusinessEntitySignal.value.id, ...ancestorBEIDsSetSignal.value])
            expandedBusinessEntitiesSetSignal.value = set

            expandedSearchOverrideSetSignal.value = new Set()

            hiddenBusinessEntitiesSetSignal.value = new Set()

            initializedSignal.value = true
            //console.log("Initialized expanded IDs")
        }
    )

    const filterTree = (filter) => {
        const helper = (node) => {
            let show = false
            if (filter && filter.length && filter.length > 0) {
                if (node.name.toLowerCase().includes(filter.toString())) {
                    show = true
                }
            }
            else {
                show = true
            }
            if (node.companies && node.companies.length && node.companies.length > 0) {
                for (let i = 0; i < node.companies.length; i++) {
                    const child = node.companies[i]
                    if (!helper(child)) {
                        show = true
                    }
                }
            }
            if (show == true) {
                hiddenBusinessEntitiesSetSignal.value.delete(node.id)
                if (filter?.length > 0) {
                    expandedSearchOverrideSetSignal.value.add(node.id)
                }
                return false
            }
            hiddenBusinessEntitiesSetSignal.value.add(node.id)
            if (expandedSearchOverrideSetSignal.value.has(node.id)) {
                expandedSearchOverrideSetSignal.value.delete(node.id)
            }
            return true
        }
        helper(rootBusinessEntitySignal.value)
    }

    const handleFilter = (event) => {
        const f = "handleFilter()"
        let print = false
        if (print) {
            console.log(`${f}: filter value changed to ${event.target.value}`)
        }
        filterSignal.value = event.target.value
        if (filterSignal.value.length == 0) {
            expandedSearchOverrideSetSignal.value = new Set()
            hiddenBusinessEntitiesSetSignal.value = new Set()
        }
        filterTree(filterSignal.value.toLowerCase())
        if (print) {
            console.log("The following BEs will be displayed regardless of expansion status:")
            console.log(Array(...expandedSearchOverrideSetSignal.value.values()))
        }
        rerender()
    }

    const handleExpandAll = (e) => {
        expandAllSignal.value = !!e.target.checked
        rerender()
    }

    const deleteBusinessEntitySearchTermsButtonStyleComputed = useComputed(
        () => {
            if (filterSignal.value) {
                return { opacity: 1, pointerEvents: "auto" }
            }
            return { opacity: 0, pointerEvents: "none" }
        }
    )

    const deleteBusinessEntitySearchTerms = () => {
        filterSignal.value = null
        document.getElementById("beSearchQuery").value = ''
        expandedSearchOverrideSetSignal.value = new Set()
        filterTree(null)
        rerender()
    }

    if (!businessEntitiesLoadedSignal.value) {
        return <></>
    }

    if (!showTreeSignal.value) {
        return (
            <Button size="sm" variant="outline-secondary" className="align-items-center justify-content-center mx-2 mt-3" onClick={() => handleToggleTree(true)}>
                <Icon icon='arrows-expand-vertical' width="16" height="16" ></Icon>
            </Button>

        )
    }

    return (
        <>
            <div className="align-items-center mx-1 mt-3">
                <Button size="sm" variant="outline-secondary" className="align-items-center justify-content-center mx-1" onClick={() => handleToggleTree(!showTreeSignal.value)}>
                    <Icon icon='arrows-expand-vertical' width="16" height="16" ></Icon>
                </Button>
                <h3 className="display-1 fs-2 px-3 my-2">Accounts</h3>
            </div>
            <InputGroup className="px-2 mb-1">
                <span style={{ width: 'calc(100% - 58px)', position: 'relative' }}>
                    <Form.Control id="beSearchQuery" aria-label="Business Entity Filter" aria-describedby="be-filter" onChange={handleFilter} value={filterSignal.value} style={{ position: "inline" }} placeholder="Filter organizations by name" />
                    <span id="deleteBusinessEntitySearchTerms" style={deleteBusinessEntitySearchTermsButtonStyleComputed.value} role="text" aria-label="Clear search terms" title="Clear search terms" onClick={deleteBusinessEntitySearchTerms}>
                        <Icon icon="delete" height={32} width={32} />
                    </span>
                </span>
                <InputGroup.Text id="be-filter">
                    <Icon icon="search" width="16" height="16"></Icon>
                </InputGroup.Text>
            </InputGroup>
            <InputGroup className="px-2">
                <Form.Check // prettier-ignore
                    type="switch"
                    id="custom-switch"
                    label="Expand All"
                    onChange={handleExpandAll}
                />
            </InputGroup>
            <Loadable loadedSignals={[businessEntitiesLoadedSignal]} errorSignals={[businessEntitiesErrorSignal]}>
                <ul className="tree be-treeview" role="tree" aria-multiselectable="false">
                    <BusinessEntityTreeElement
                        businessEntity={rootBusinessEntitySignal.value}
                        depth={0}
                        expandedBusinessEntitiesSetSignal={expandedBusinessEntitiesSetSignal}
                        expandedSearchOverrideSetSignal={expandedSearchOverrideSetSignal}
                        expandAllSignal={expandAllSignal}
                        rerender={rerender}
                        key={treeKeyComputed.value}
                        childNum={1}
                        hiddenBusinessEntitiesSetSignal={hiddenBusinessEntitiesSetSignal}
                    ></BusinessEntityTreeElement>
                </ul>
            </Loadable>
        </>
    )
}

export default BusinessEntityTreeRoot